var __defProp = Object.defineProperty;
var __name = (target, value) => __defProp(target, "name", { value, configurable: true });
import { z } from "zod/v4";
const strokeDashTypesZodType = z.union([
  z.literal("Solid"),
  z.literal("ShortDash"),
  z.literal("ShortDash2"),
  z.literal("ShortDot"),
  z.literal("ShortDashDot"),
  z.literal("ShortDashDotDot"),
  z.literal("Dot"),
  z.literal("Dash"),
  z.literal("LongDash"),
  z.literal("DashDot"),
  z.literal("LongDashDot"),
  z.literal("LongDashDotDot")
]);
const yAccessorValueZodType = z.union([z.string(), z.number()]);
const CoordinateZodType = z.tuple([z.number(), z.number()]);
const LineWidthDashOptionZodType = z.object({
  strokeWidth: z.coerce.number(),
  strokeDasharray: strokeDashTypesZodType
});
const LineOptionZodType = LineWidthDashOptionZodType.extend({
  /** #rgba hex format */
  strokeStyle: z.string()
});
const ShowLineOptionZodType = LineOptionZodType.extend({
  show: z.boolean()
});
const AltLineOptionZodType = ShowLineOptionZodType.extend({
  strokeStyle2: z.string()
});
const ShowColorOptionZodType = z.object({
  show: z.boolean(),
  /** #rgba hex format */
  color: z.string()
});
const ColorOptionZodType = z.object({
  /** #rgba hex format */
  color: z.string()
});
const ShowValueColorOptionZodType = ShowColorOptionZodType.extend({
  /** level ratio */
  value: z.number()
});
const ShowOpacityOptionZodType = z.object({
  show: z.boolean(),
  /** float between 0 ~ 1 */
  opacity: z.number()
});
const FontOptionZodType = z.object({
  /** #rgba hex format */
  fill: z.string(),
  fontFamily: z.string(),
  /** pixel unit */
  fontSize: z.number(),
  fontWeight: z.union([z.string(), z.number()]),
  /** supports only 'italic' */
  fontStyle: z.string()
});
const FontBasicOptionZodType = FontOptionZodType.omit({
  fontFamily: true
});
var VertexType = /* @__PURE__ */ ((VertexType2) => {
  VertexType2["NORMAL"] = "normal";
  VertexType2["ARROW"] = "arrow";
  return VertexType2;
})(VertexType || {});
var ShapeType = /* @__PURE__ */ ((ShapeType2) => {
  ShapeType2["MEASURE"] = "Measure";
  ShapeType2["TREND_LINE"] = "TrendLine";
  ShapeType2["RAY"] = "Ray";
  ShapeType2["INFO_LINE"] = "InfoLine";
  ShapeType2["HORZ_LINE"] = "HorzLine";
  ShapeType2["VERT_LINE"] = "VertLine";
  ShapeType2["PARALLEL_CHANNEL"] = "ParallelChannel";
  ShapeType2["FIB_RETRACEMENT"] = "FibRetracement";
  ShapeType2["FIB_CHANNEL"] = "FibChannel";
  ShapeType2["GANN_BOX"] = "GannBox";
  ShapeType2["GANN_FAN"] = "GannFan";
  ShapeType2["ELLIOTT_IMPULSE_12345"] = "ElliottImpulse12345";
  ShapeType2["ELLIOTT_CORRECTION_ABC"] = "ElliottCorrectionABC";
  ShapeType2["XABCD_PATTERN"] = "XABCDPattern";
  ShapeType2["RECTANGLE"] = "Rectangle";
  ShapeType2["TRIANGLE"] = "Triangle";
  ShapeType2["PATH"] = "Path";
  ShapeType2["TEXT"] = "Text";
  ShapeType2["FIXED_RANGE_VOLUME_PROFILE"] = "FixedRangeVolumeProfile";
  ShapeType2["ANCHORED_VOLUME_PROFILE"] = "AnchoredVolumeProfile";
  ShapeType2["ORDER_LINE"] = "OrderLine";
  return ShapeType2;
})(ShapeType || {});
const CONDITIONABLE_SHAPE_TYPES = [
  "TrendLine" /* TREND_LINE */,
  "Ray" /* RAY */,
  "InfoLine" /* INFO_LINE */,
  "HorzLine" /* HORZ_LINE */,
  "ParallelChannel" /* PARALLEL_CHANNEL */,
  "FibRetracement" /* FIB_RETRACEMENT */,
  "FibChannel" /* FIB_CHANNEL */,
  "GannBox" /* GANN_BOX */,
  "GannFan" /* GANN_FAN */,
  "Rectangle" /* RECTANGLE */,
  "Triangle" /* TRIANGLE */
];
function isConditionable(shapeType) {
  return CONDITIONABLE_SHAPE_TYPES.includes(shapeType);
}
__name(isConditionable, "isConditionable");
const VertexTypeZodType = z.enum(VertexType);
const ArrowZodType = z.tuple([VertexTypeZodType, VertexTypeZodType]);
const ExtendZodType = z.tuple([z.boolean(), z.boolean()]);
const BaseStorageShapeZodType = z.object({
  uuid: z.string(),
  /** 사용자 표시용 ID (조건 편집기 등에서 참조). 서버에서 자동 발급. */
  id: z.number().optional(),
  type: z.enum(ShapeType),
  chartId: z.templateLiteral(["chartId_", z.string()]),
  coordinates: z.array(CoordinateZodType),
  order: z.number().optional(),
  createdAt: z.number(),
  updatedAt: z.number(),
  /** 사용자가 도형 목록에서 숨김 처리한 상태. F5 갱신 후에도 유지되어야 하므로 영구 저장. */
  hidden: z.boolean().optional(),
  /** 사용자가 도형을 잠금 처리한 상태. F5 갱신 후에도 유지되어야 하므로 영구 저장. */
  locked: z.boolean().optional()
});
const ExtensibleInfoLineFieldsZodType = z.object({
  line: LineOptionZodType,
  arrow: ArrowZodType,
  extend: ExtendZodType,
  showPriceRange: z.boolean(),
  showPercentChange: z.boolean(),
  showBarsRange: z.boolean(),
  showDateTimeRange: z.boolean(),
  showAngle: z.boolean(),
  alwaysShow: z.boolean()
});
const TrendLineStorageShapeZodType = BaseStorageShapeZodType.extend(
  ExtensibleInfoLineFieldsZodType.shape
).extend({
  type: z.literal("TrendLine" /* TREND_LINE */)
});
const RayStorageShapeZodType = BaseStorageShapeZodType.extend(ExtensibleInfoLineFieldsZodType.shape).extend({
  type: z.literal("Ray" /* RAY */)
});
const InfoLineStorageShapeZodType = BaseStorageShapeZodType.extend(ExtensibleInfoLineFieldsZodType.shape).extend(
  {
    type: z.literal("InfoLine" /* INFO_LINE */)
  }
);
const HorzLineStorageShapeZodType = BaseStorageShapeZodType.extend({
  type: z.literal("HorzLine" /* HORZ_LINE */),
  line: LineOptionZodType
});
const VertLineStorageShapeZodType = BaseStorageShapeZodType.extend({
  type: z.literal("VertLine" /* VERT_LINE */),
  line: LineOptionZodType,
  /** VertLine이 그려진 원본 chartId (chartId_common과 별도) */
  originChartId: z.templateLiteral(["chartId_", z.string()]).optional()
});
const ElliottImpulseStorageShapeZodType = BaseStorageShapeZodType.extend({
  type: z.literal("ElliottImpulse12345" /* ELLIOTT_IMPULSE_12345 */),
  line: LineOptionZodType
});
const ElliottCorrectionABCStorageShapeZodType = BaseStorageShapeZodType.extend({
  type: z.literal("ElliottCorrectionABC" /* ELLIOTT_CORRECTION_ABC */),
  line: LineOptionZodType
});
const FibRetracementStorageShapeZodType = BaseStorageShapeZodType.extend({
  type: z.literal("FibRetracement" /* FIB_RETRACEMENT */),
  trendLine: LineOptionZodType,
  levelsLine: LineWidthDashOptionZodType,
  extend: ExtendZodType,
  levels: z.array(ShowValueColorOptionZodType),
  background: ShowOpacityOptionZodType
});
const FibChannelStorageShapeZodType = BaseStorageShapeZodType.extend({
  type: z.literal("FibChannel" /* FIB_CHANNEL */),
  levelsLine: LineWidthDashOptionZodType,
  extend: ExtendZodType,
  levels: z.array(ShowValueColorOptionZodType),
  background: ShowOpacityOptionZodType
});
const GannBoxStorageShapeZodType = BaseStorageShapeZodType.extend({
  type: z.literal("GannBox" /* GANN_BOX */),
  priceLevels: z.array(ShowValueColorOptionZodType),
  priceBackground: ShowOpacityOptionZodType,
  timeLevels: z.array(ShowValueColorOptionZodType),
  timeBackground: ShowOpacityOptionZodType
});
const GANN_FAN_LABELS = ["1/8", "1/4", "1/3", "1/2", "1/1", "2/1", "3/1", "4/1", "8/1"];
const GannFanStorageShapeZodType = BaseStorageShapeZodType.extend({
  type: z.literal("GannFan" /* GANN_FAN */),
  levels: z.array(ShowColorOptionZodType),
  background: ShowOpacityOptionZodType
});
const TextStorageShapeZodType = BaseStorageShapeZodType.extend({
  type: z.literal("Text" /* TEXT */),
  text: z.string(),
  font: FontOptionZodType,
  background: ShowColorOptionZodType
});
const ParallelChannelStorageShapeZodType = BaseStorageShapeZodType.extend({
  type: z.literal("ParallelChannel" /* PARALLEL_CHANNEL */),
  channel: LineOptionZodType,
  middle: ShowLineOptionZodType,
  extend: ExtendZodType,
  background: ShowColorOptionZodType
});
const RectangleStorageShapeZodType = BaseStorageShapeZodType.extend({
  type: z.literal("Rectangle" /* RECTANGLE */),
  coordinates: z.array(CoordinateZodType),
  border: LineOptionZodType,
  middle: ShowLineOptionZodType,
  background: ShowColorOptionZodType,
  extend: ExtendZodType
});
const TriangleStorageShapeZodType = BaseStorageShapeZodType.extend({
  type: z.literal("Triangle" /* TRIANGLE */),
  coordinates: z.array(CoordinateZodType),
  border: LineOptionZodType,
  background: ShowColorOptionZodType
});
const PathStorageShapeZodType = BaseStorageShapeZodType.extend({
  type: z.literal("Path" /* PATH */),
  coordinates: z.array(CoordinateZodType),
  line: LineOptionZodType,
  arrow: ArrowZodType
});
const XABCDPatternStorageShapeZodType = BaseStorageShapeZodType.extend({
  type: z.literal("XABCDPattern" /* XABCD_PATTERN */),
  coordinates: z.array(CoordinateZodType),
  label: FontOptionZodType,
  border: LineOptionZodType,
  background: ShowColorOptionZodType
});
const VolumeProfileSettingsZodType = z.object({
  rowsLayout: z.union([z.literal("Number of Rows"), z.literal("Ticks per Row")]),
  rowSize: z.number(),
  volume: z.union([z.literal("Up/Down"), z.literal("Total"), z.literal("Delta")]),
  valueAreaVolume: z.number(),
  volumeProfile: z.boolean(),
  values: ShowColorOptionZodType,
  width: z.number(),
  placement: z.union([z.literal("left"), z.literal("right")]),
  upVolume: z.string(),
  downVolume: z.string(),
  valueAreaUp: z.string(),
  valueAreaDown: z.string(),
  vah: ShowLineOptionZodType,
  val: ShowLineOptionZodType,
  poc: ShowLineOptionZodType,
  developingPOC: ShowLineOptionZodType,
  developingVA: ShowLineOptionZodType,
  histogramBox: z.string()
});
const FixedRangeVolumeProfileStorageShapeZodType = BaseStorageShapeZodType.extend(
  VolumeProfileSettingsZodType.shape
).extend({
  type: z.literal("FixedRangeVolumeProfile" /* FIXED_RANGE_VOLUME_PROFILE */)
});
const AnchoredVolumeProfileStorageShapeZodType = BaseStorageShapeZodType.extend(
  VolumeProfileSettingsZodType.shape
).extend({
  type: z.literal("AnchoredVolumeProfile" /* ANCHORED_VOLUME_PROFILE */)
});
const OrderLineStorageShapeZodType = BaseStorageShapeZodType.extend({
  type: z.literal("OrderLine" /* ORDER_LINE */),
  positionType: z.union([z.literal("long"), z.literal("short")]),
  price: z.number(),
  amount: z.number(),
  tpsl: z.array(z.object({ tp: z.number(), sl: z.number() }))
});
const StorageShapeInputZodType = z.discriminatedUnion("type", [
  TrendLineStorageShapeZodType,
  RayStorageShapeZodType,
  InfoLineStorageShapeZodType,
  HorzLineStorageShapeZodType,
  VertLineStorageShapeZodType,
  ElliottImpulseStorageShapeZodType,
  ElliottCorrectionABCStorageShapeZodType,
  FibRetracementStorageShapeZodType,
  FibChannelStorageShapeZodType,
  GannBoxStorageShapeZodType,
  GannFanStorageShapeZodType,
  TextStorageShapeZodType,
  ParallelChannelStorageShapeZodType,
  RectangleStorageShapeZodType,
  TriangleStorageShapeZodType,
  PathStorageShapeZodType,
  XABCDPatternStorageShapeZodType,
  FixedRangeVolumeProfileStorageShapeZodType,
  AnchoredVolumeProfileStorageShapeZodType,
  OrderLineStorageShapeZodType
]);
const StorageShapesMapZodType = z.record(
  z.templateLiteral(["chartId_", z.string()]),
  z.partialRecord(z.enum(ShapeType), z.array(StorageShapeInputZodType))
);
export {
  AltLineOptionZodType,
  AnchoredVolumeProfileStorageShapeZodType,
  CONDITIONABLE_SHAPE_TYPES,
  ColorOptionZodType,
  CoordinateZodType,
  ElliottCorrectionABCStorageShapeZodType,
  ElliottImpulseStorageShapeZodType,
  FibChannelStorageShapeZodType,
  FibRetracementStorageShapeZodType,
  FixedRangeVolumeProfileStorageShapeZodType,
  FontBasicOptionZodType,
  FontOptionZodType,
  GANN_FAN_LABELS,
  GannBoxStorageShapeZodType,
  GannFanStorageShapeZodType,
  HorzLineStorageShapeZodType,
  InfoLineStorageShapeZodType,
  LineOptionZodType,
  LineWidthDashOptionZodType,
  OrderLineStorageShapeZodType,
  ParallelChannelStorageShapeZodType,
  PathStorageShapeZodType,
  RayStorageShapeZodType,
  RectangleStorageShapeZodType,
  ShapeType,
  ShowColorOptionZodType,
  ShowLineOptionZodType,
  ShowOpacityOptionZodType,
  ShowValueColorOptionZodType,
  StorageShapeInputZodType,
  StorageShapesMapZodType,
  TextStorageShapeZodType,
  TrendLineStorageShapeZodType,
  TriangleStorageShapeZodType,
  VertLineStorageShapeZodType,
  VertexType,
  XABCDPatternStorageShapeZodType,
  isConditionable,
  strokeDashTypesZodType,
  yAccessorValueZodType
};
//# sourceMappingURL=shape.js.map
